import { FieldInfo } from '@/components/FieldInfo';
import { studyQueryKeys } from '@/config/queryKeys';
import { apiClient } from '@/lib/api-client';
import type { StudyCreateResponse } from '@/types';
import { Button } from '@nautilusbio/nb-design-system/Button';
import type { TextSizes } from '@nautilusbio/nb-design-system/helpers';
import { Input } from '@nautilusbio/nb-design-system/Input';
import { Textarea } from '@nautilusbio/nb-design-system/Textarea';
import { toast } from '@nautilusbio/nb-design-system/Toaster';
import { useForm } from '@tanstack/react-form';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import clsx from 'clsx';
import { Loader } from 'lucide-react';
import React, { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import createStyles from '../../../components/Create/styles.module.css';

type DataRow = {
  description: string;
  name: string;
};

const nameSchema = z
  .string()
  .min(3, 'Name must be at least 3 characters long.')
  .refine(s => !s.includes("'"), "Name cannot contain apostrophes (').");

const descriptionSchema = z
  .string()
  .min(1, 'Name must be at least 1 character long.')
  .refine(s => !s.includes("'"), "Description cannot contain apostrophes (').");

const CreateForm: FC<{ onSuccess: () => void; size?: TextSizes; userOrg: string }> = ({
  onSuccess,
  size = 'xl',
  userOrg,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const addStudy = useMutation({
    mutationFn: async (newStudy: DataRow): Promise<StudyCreateResponse> => {
      const response = await apiClient.post<StudyCreateResponse>(studyQueryKeys.endpoints.list(userOrg), newStudy);

      if (!response) {
        throw new Error('Study creation failed - no response from server');
      }

      return response;
    },
    scope: {
      id: 'studies',
    },
  });

  const form = useForm({
    defaultValues: {
      description: '',
      name: '',
    },
    onSubmit: async ({ value }) => {
      const studyPromise = addStudy.mutateAsync(value);

      toast.promise(studyPromise, {
        loading: t('study.creating', {
          ns: 'create',
        }),
        success: data => {
          // Reset Form
          form.reset();
          onSuccess();
          navigate({
            params: {
              studyId: data.id,
            },
            to: '/studies/$studyId',
          });
          // Update Toast message
          return `${value.name} ${t('study.success', {
            ns: 'create',
          })}`;
        },
        error: () =>
          t('study.failed', {
            ns: 'create',
          }),
      });
    },
  });

  return (
    <div className={createStyles.formContainer}>
      <form
        className={createStyles.form}
        onSubmit={async e => {
          e.preventDefault();
          e.stopPropagation();
          await form.handleSubmit();
        }}
      >
        <form.Field
          name="name"
          validators={{
            onChange: nameSchema,
            onMount: nameSchema,
          }}
        >
          {field => {
            const label = {
              children: t('general:name'),
              htmlFor: field.name,
              id: `label-${field.name}`,
            };

            return (
              <>
                <Input
                  autoComplete="off"
                  // oxlint-disable-next-line no-autofocus
                  autoFocus={true}
                  className={createStyles.uiInput}
                  data-testid={`input-${field.name}`}
                  id={field.name}
                  label={label}
                  name={field.name}
                  onBlur={field.handleBlur}
                  onChange={e => field.handleChange(e.target.value)}
                  size={size}
                  value={field.state.value}
                />

                <FieldInfo field={field} />
              </>
            );
          }}
        </form.Field>

        <form.Field
          name="description"
          validators={{
            onChange: descriptionSchema,
            onMount: descriptionSchema,
          }}
        >
          {field => {
            const label = {
              children: t('general:desc'),
              htmlFor: field.name,
              id: `label-${field.name}`,
            };

            return (
              <>
                <Textarea
                  autoComplete="off"
                  className={createStyles.uiInput}
                  data-testid={`input-${field.name}`}
                  id={field.name}
                  label={label}
                  name={field.name}
                  onBlur={field.handleBlur}
                  onChange={({ target }: { target: { value: string } }) => field.handleChange(target.value)}
                  rows={5}
                  size={size}
                  value={field.state.value}
                />

                <FieldInfo field={field} />
              </>
            );
          }}
        </form.Field>

        <form.Subscribe selector={state => [state.canSubmit, state.isSubmitting, state.isSubmitted]}>
          {([canSubmit, isSubmitting, isSubmitted]) => (
            <div className={createStyles.footer}>
              <Button
                className={clsx((isSubmitting || isSubmitted) && createStyles.noPointer)}
                data-testid="submit-btn"
                isDisabled={!canSubmit}
                size="xl"
                type="submit"
                visual={isSubmitting || isSubmitted ? 'success' : 'primary'}
              >
                {isSubmitting || isSubmitted ? (
                  <>
                    <Loader className={createStyles.spinner} /> &nbsp;{t('general:submitting')}
                  </>
                ) : (
                  t('general:submit')
                )}
              </Button>
            </div>
          )}
        </form.Subscribe>
      </form>
    </div>
  );
};

export default CreateForm;
